import { useContext } from "react";
import { ButtonDS, GlobalIcon } from "get-life-storybook-ts";
import { I18nContext } from "../../../../../../context/i18n.context";
import { cardStyle } from "../cardStyles";
import { Beneficiary, RELATIONSHIPS } from "./constants";

interface BeneficiaryListProps {
  beneficiaries: Beneficiary[];
  deleteConfirmId: string | null;
  showForm: boolean;
  onEdit: (b: Beneficiary) => void;
  onRequestDelete: (id: string) => void;
  onConfirmDelete: (id: string) => void;
  onCancelDelete: () => void;
  onAdd: () => void;
}

const BeneficiaryList = ({
  beneficiaries,
  deleteConfirmId,
  showForm,
  onEdit,
  onRequestDelete,
  onConfirmDelete,
  onCancelDelete,
  onAdd,
}: BeneficiaryListProps) => {
  const {
    state: { translate },
  } = useContext(I18nContext);

  const getRelationshipLabel = (value: string) => {
    const rel = RELATIONSHIPS.find((r) => r.value === value);
    return rel ? translate(rel.key) : value;
  };

  return (
    <div style={cardStyle}>
      <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
        {beneficiaries.map((b, idx) => {
          const isLast = idx === beneficiaries.length - 1;
          const borderBottom = isLast ? "none" : "1px solid #f3f4f6";

          if (deleteConfirmId === b.id) {
            return (
              <div
                key={b.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: 12,
                  padding: "16px 0",
                  borderBottom,
                }}
              >
                <p style={{ fontSize: 14, color: "#991b1b", margin: 0, flex: 1 }}>
                  {translate("newDashboard.beneficiaryManager.deleteConfirm").replace(
                    "{name}",
                    b.name
                  )}
                </p>
                <div style={{ display: "flex", gap: 8 }}>
                  <ButtonDS
                    label={translate("newDashboard.beneficiaryManager.delete")}
                    buttonType="primary"
                    size="32"
                    onClick={() => onConfirmDelete(b.id)}
                    className="!bg-red-600 !border-red-600"
                  />
                  <ButtonDS
                    label={translate("newDashboard.beneficiaryManager.cancel")}
                    buttonType="secondary"
                    size="32"
                    onClick={onCancelDelete}
                  />
                </div>
              </div>
            );
          }

          return (
            <div
              key={b.id}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "16px 0",
                borderBottom,
                gap: 12,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 12, flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: "50%",
                    backgroundColor: "#eef2ff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <GlobalIcon iconName="SimpleUserIcon" size="XS" color="#4f46e5" />
                </div>
                <div style={{ minWidth: 0 }}>
                  <p
                    style={{
                      fontSize: 15,
                      fontWeight: 600,
                      color: "#111827",
                      margin: 0,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {b.name}
                  </p>
                  <p style={{ fontSize: 13, color: "#9ca3af", margin: 0 }}>
                    {getRelationshipLabel(b.relationship)}
                  </p>
                </div>
              </div>
              <span
                style={{
                  fontSize: 16,
                  fontWeight: 700,
                  color: "#4f46e5",
                  backgroundColor: "#eef2ff",
                  padding: "6px 14px",
                  borderRadius: 8,
                  flexShrink: 0,
                }}
              >
                {b.percentage}%
              </span>
              <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
                <button
                  onClick={() => onEdit(b)}
                  style={{
                    border: "none",
                    background: "none",
                    cursor: "pointer",
                    padding: 6,
                    borderRadius: 6,
                    display: "flex",
                  }}
                  title={translate("newDashboard.beneficiaryManager.edit")}
                >
                  <GlobalIcon iconName="PencilIcon" size="XS" color="#6b7280" />
                </button>
                <button
                  onClick={() => onRequestDelete(b.id)}
                  style={{
                    border: "none",
                    background: "none",
                    cursor: "pointer",
                    padding: 6,
                    borderRadius: 6,
                    display: "flex",
                  }}
                  title={translate("newDashboard.beneficiaryManager.delete")}
                >
                  <GlobalIcon iconName="TrashIcon" size="XS" color="#ef4444" />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {!showForm && (
        <div style={{ marginTop: 16 }}>
          <ButtonDS
            label={translate("newDashboard.beneficiaryManager.addButton")}
            buttonType="secondary"
            size="32"
            onClick={onAdd}
          />
        </div>
      )}
    </div>
  );
};

export default BeneficiaryList;
